/**
 * Assembly node for the pipeline canvas DAG.
 *
 * Appears automatically when the pipeline targets a schema with output_structure.
 * Shows assembly status, output format, nesting summary, and byte size.
 */
import React from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { Handle, Position } from 'reactflow';
import type { NodeProps } from 'reactflow';
import { AccountTree as AssemblyIcon, Download as DownloadIcon } from '@mui/icons-material';

// Brand color for assembly nodes — distinct from the source group (blue)
// and the target schema container (emerald) so users can tell the three
// container-style nodes apart at a glance regardless of status.
const ASSEMBLY_BRAND = '#6366f1'; // indigo-500

export interface AssemblyNodeData {
  title: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  /** Output format badge text, e.g. "XML", "JSON". */
  outputFormat?: string;
  /** Short nesting summary, e.g. "6 levels: ClinicalData > Subject > ...". */
  nestingSummary?: string;
  /** Output count — for tabular formats this is rows; for hierarchical
   *  XML/JSON it's total element instances across all levels. */
  rowCount?: number;
  /** Output byte size. */
  byteSize?: number;
  /** Error message when status=failed. */
  error?: string;
  /** One-click download; when provided, a download icon appears on the node. */
  onDownload?: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#94a3b8',    // slate
  running: '#a855f7',    // purple (animated pulse)
  completed: '#22c55e',  // green
  failed: '#ef4444',     // red
};

const AssemblyNode: React.FC<NodeProps<AssemblyNodeData>> = ({ data, selected, sourcePosition, targetPosition }) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const sourceHandlePos = sourcePosition ?? Position.Bottom;
  const statusColor = STATUS_COLORS[data.status] || '#94a3b8';
  const isRunning = data.status === 'running';

  return (
    <Box
      sx={{
        width: 240,
        bgcolor: 'background.paper',
        borderRadius: 0.5,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        // Brand-indigo left border so this node reads as "Assembly" regardless
        // of lifecycle status — the status is conveyed by the dot in the
        // header instead.
        borderLeft: `4px solid ${ASSEMBLY_BRAND}`,
        boxShadow: selected ? 3 : 1,
        overflow: 'hidden',
        ...(isRunning && {
          animation: 'assemblyPulse 1.5s ease-in-out infinite',
          '@keyframes assemblyPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0px ${alpha(ASSEMBLY_BRAND, 0.0)}` },
            '50%': { boxShadow: `0 0 0 4px ${alpha(ASSEMBLY_BRAND, 0.18)}` },
          },
        }),
      }}
    >
      <Handle type="target" position={targetHandlePos} style={{ visibility: 'hidden' }} />

      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, bgcolor: alpha(ASSEMBLY_BRAND, 0.06), borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              bgcolor: alpha(ASSEMBLY_BRAND, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AssemblyIcon sx={{ fontSize: 14, color: ASSEMBLY_BRAND }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: 12 }}>
              {data.title || 'Hierarchical Assembly'}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: statusColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontSize: 9, color: ASSEMBLY_BRAND, fontVariant: 'small-caps', letterSpacing: 0.5, fontWeight: 600 }}
              >
                ASSEMBLY
              </Typography>
            </Stack>
          </Box>
          {data.outputFormat && (
            <Chip label={data.outputFormat.toUpperCase()} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          )}
          {data.onDownload && data.status === 'completed' && (
            <Tooltip title={`Download ${(data.outputFormat || 'file').toUpperCase()}`}>
              <IconButton
                size="small"
                sx={{ p: 0.25 }}
                onClick={(e) => { e.stopPropagation(); data.onDownload?.(); }}
              >
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, py: 1 }}>
        {data.nestingSummary && (
          <Typography variant="caption" color="text.secondary" noWrap title={data.nestingSummary}>
            {data.nestingSummary}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          {data.rowCount != null && data.rowCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {data.rowCount.toLocaleString()}
              {' '}
              {/* For hierarchical formats the count is element instances,
                  not rows; pick the label per output format. */}
              {data.outputFormat && /^(xml|json)$/i.test(data.outputFormat)
                ? 'instances'
                : 'rows'}
            </Typography>
          )}
          {data.byteSize != null && (
            <Typography variant="caption" color="text.secondary">
              {(data.byteSize / 1024).toFixed(1)} KB
            </Typography>
          )}
        </Stack>

        {data.status === 'failed' && data.error && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }} noWrap title={data.error}>
            {data.error}
          </Typography>
        )}
      </Box>

      <Handle type="source" position={sourceHandlePos} style={{ visibility: 'hidden' }} />
    </Box>
  );
};

export default AssemblyNode;
